import { useReducer, useCallback, useRef, useEffect } from "react";
import type {
  ArborInspectionDetail,
  ArborInspectionUpdateInput,
  MeasurementInput,
  StemMeasurementInput,
  HealthAssessmentInput,
  HealthFindingInput,
  MechanicalFindingInput,
  RiskAssessmentInput,
  RiskTargetInput,
  InterferenceInput,
  RecommendationInput,
  CompensationInput,
} from "@/types/arboricultural-inspection";
import { inspectionApi } from "@/lib/api";

// --- Form state ---

export interface InspectionFormState {
  // Step 1 — Identificação
  locationConfirmed: boolean;
  botanicalIdentificationStatus: string;
  botanicalNotes: string;

  // Step 2 — Dendrometria e Botânica
  measurement: MeasurementInput;
  stems: StemMeasurementInput[];

  // Step 3 — Condição Fitossanitária
  healthAssessment: HealthAssessmentInput;
  healthFindings: HealthFindingInput[];

  // Step 4 — Condição Biomecânica
  mechanicalFindings: MechanicalFindingInput[];

  // Step 5 — Conflitos Urbanos
  interferences: InterferenceInput[];

  // Step 6 — Avaliação de Risco
  riskAssessment: RiskAssessmentInput;
  riskTargets: RiskTargetInput[];

  // Step 7 — Parecer e Recomendações
  diagnosis: string;
  technicalOpinion: string;
  recommendations: RecommendationInput[];
  compensation: CompensationInput;

  // Step 8 — Evidências (upload-only, managed via API)

  // Step 9 — Responsabilidade Técnica
  inspectorName: string;
  professionalCouncil: string;
  professionalRegistration: string;

  // Step 10 — Revisão (read-only, no state)

  // Meta
  currentStep: number;
  isDirty: boolean;
  lastSavedAt: Date | null;
  saveStatus: "idle" | "saving" | "saved" | "error";
  /** Explicacao do servidor quando o salvamento falha (ex.: campo fora do limite). */
  saveError: string | null;
  validationErrors: Record<string, string[]>;
  inspectionDate: string;
  notes: string;
  requiresMonitoring: boolean;
  nextInspectionSuggestedAt: string;
  // A compensacao so e enviada se indicada ou se o tecnico mexeu nela: assim desmarcar
  // persiste e uma vistoria sem compensacao nao ganha uma linha vazia.
  compensationTouched: boolean;
}

// --- Actions ---

export type InspectionFormAction =
  | { type: "SET_FIELD"; field: string; value: unknown }
  | { type: "SET_MEASUREMENT"; field: keyof MeasurementInput; value: unknown }
  | { type: "SET_HEALTH"; field: keyof HealthAssessmentInput; value: unknown }
  | { type: "SET_RISK"; field: keyof RiskAssessmentInput; value: unknown }
  | { type: "SET_COMPENSATION"; field: keyof CompensationInput; value: unknown }
  | { type: "ADD_STEM"; stem: StemMeasurementInput }
  | { type: "REMOVE_STEM"; index: number }
  | { type: "UPDATE_STEM"; index: number; field: string; value: unknown }
  | { type: "ADD_HEALTH_FINDING"; finding: HealthFindingInput }
  | { type: "REMOVE_HEALTH_FINDING"; index: number }
  | { type: "UPDATE_HEALTH_FINDING"; index: number; field: string; value: unknown }
  | { type: "ADD_MECHANICAL_FINDING"; finding: MechanicalFindingInput }
  | { type: "REMOVE_MECHANICAL_FINDING"; index: number }
  | { type: "UPDATE_MECHANICAL_FINDING"; index: number; field: string; value: unknown }
  | { type: "ADD_INTERFERENCE"; interference: InterferenceInput }
  | { type: "REMOVE_INTERFERENCE"; index: number }
  | { type: "UPDATE_INTERFERENCE"; index: number; field: string; value: unknown }
  | { type: "ADD_RISK_TARGET"; target: RiskTargetInput }
  | { type: "REMOVE_RISK_TARGET"; index: number }
  | { type: "UPDATE_RISK_TARGET"; index: number; field: string; value: unknown }
  | { type: "ADD_RECOMMENDATION"; rec: RecommendationInput }
  | { type: "REMOVE_RECOMMENDATION"; index: number }
  | { type: "UPDATE_RECOMMENDATION"; index: number; field: string; value: unknown }
  | { type: "SET_STEP"; step: number }
  | { type: "MARK_SAVED"; savedAt: Date }
  | { type: "SET_SAVE_STATUS"; status: InspectionFormState["saveStatus"]; message?: string | null }
  | { type: "SET_VALIDATION_ERRORS"; errors: Record<string, string[]> }
  | { type: "LOAD_FROM_API"; detail: ArborInspectionDetail }
  | { type: "CALCULATE_DAP"; capCm: number };

// --- Initial state ---

export function createInitialState(): InspectionFormState {
  return {
    locationConfirmed: false,
    botanicalIdentificationStatus: "",
    botanicalNotes: "",
    measurement: {},
    stems: [],
    healthAssessment: {},
    healthFindings: [],
    mechanicalFindings: [],
    interferences: [],
    riskAssessment: {},
    riskTargets: [],
    diagnosis: "",
    technicalOpinion: "",
    recommendations: [],
    compensation: { compensationIndicated: false },
    inspectorName: "",
    professionalCouncil: "",
    professionalRegistration: "",
    currentStep: 0,
    isDirty: false,
    lastSavedAt: null,
    saveStatus: "idle",
    saveError: null,
    validationErrors: {},
    inspectionDate: new Date().toISOString().split("T")[0],
    notes: "",
    requiresMonitoring: false,
    nextInspectionSuggestedAt: "",
    compensationTouched: false,
  };
}

// --- Reducer ---

function updateArray<T>(arr: T[], index: number, field: string, value: unknown): T[] {
  return arr.map((item, i) => (i === index ? { ...item, [field]: value } : item));
}

export function inspectionFormReducer(
  state: InspectionFormState,
  action: InspectionFormAction
): InspectionFormState {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value, isDirty: true };

    case "SET_MEASUREMENT":
      return {
        ...state,
        measurement: {
          ...state.measurement,
          [action.field]: action.value,
          // DAP digitado sobrescreve o calculado: a origem da medida tem de acompanhar,
          // senao o registro diria "calculado do CAP" para um valor medido a mao.
          ...(action.field === "dapCm" ? { measurementSource: action.value == null ? null : "DIRECT" } : {}),
        },
        isDirty: true,
      };

    case "SET_HEALTH":
      return {
        ...state,
        healthAssessment: { ...state.healthAssessment, [action.field]: action.value },
        isDirty: true,
      };

    case "SET_RISK":
      return {
        ...state,
        riskAssessment: { ...state.riskAssessment, [action.field]: action.value },
        isDirty: true,
      };

    case "SET_COMPENSATION":
      return {
        ...state,
        compensation: { ...state.compensation, [action.field]: action.value },
        compensationTouched: true,
        isDirty: true,
      };

    case "CALCULATE_DAP":
      return {
        ...state,
        measurement: {
          ...state.measurement,
          capCm: action.capCm,
          dapCm: Math.round((action.capCm / Math.PI) * 100) / 100,
          measurementSource: "CALCULATED_FROM_CAP",
        },
        isDirty: true,
      };

    case "ADD_STEM":
      return { ...state, stems: [...state.stems, action.stem], isDirty: true };
    case "REMOVE_STEM":
      return { ...state, stems: state.stems.filter((_, i) => i !== action.index), isDirty: true };
    case "UPDATE_STEM":
      return { ...state, stems: updateArray(state.stems, action.index, action.field, action.value), isDirty: true };

    case "ADD_HEALTH_FINDING":
      return { ...state, healthFindings: [...state.healthFindings, action.finding], isDirty: true };
    case "REMOVE_HEALTH_FINDING":
      return { ...state, healthFindings: state.healthFindings.filter((_, i) => i !== action.index), isDirty: true };
    case "UPDATE_HEALTH_FINDING":
      return { ...state, healthFindings: updateArray(state.healthFindings, action.index, action.field, action.value), isDirty: true };

    case "ADD_MECHANICAL_FINDING":
      return { ...state, mechanicalFindings: [...state.mechanicalFindings, action.finding], isDirty: true };
    case "REMOVE_MECHANICAL_FINDING":
      return { ...state, mechanicalFindings: state.mechanicalFindings.filter((_, i) => i !== action.index), isDirty: true };
    case "UPDATE_MECHANICAL_FINDING":
      return { ...state, mechanicalFindings: updateArray(state.mechanicalFindings, action.index, action.field, action.value), isDirty: true };

    case "ADD_INTERFERENCE":
      return { ...state, interferences: [...state.interferences, action.interference], isDirty: true };
    case "REMOVE_INTERFERENCE":
      return { ...state, interferences: state.interferences.filter((_, i) => i !== action.index), isDirty: true };
    case "UPDATE_INTERFERENCE":
      return { ...state, interferences: updateArray(state.interferences, action.index, action.field, action.value), isDirty: true };

    case "ADD_RISK_TARGET":
      return { ...state, riskTargets: [...state.riskTargets, action.target], isDirty: true };
    case "REMOVE_RISK_TARGET":
      return { ...state, riskTargets: state.riskTargets.filter((_, i) => i !== action.index), isDirty: true };
    case "UPDATE_RISK_TARGET":
      return { ...state, riskTargets: updateArray(state.riskTargets, action.index, action.field, action.value), isDirty: true };

    case "ADD_RECOMMENDATION":
      return { ...state, recommendations: [...state.recommendations, action.rec], isDirty: true };
    case "REMOVE_RECOMMENDATION":
      return { ...state, recommendations: state.recommendations.filter((_, i) => i !== action.index), isDirty: true };
    case "UPDATE_RECOMMENDATION":
      return { ...state, recommendations: updateArray(state.recommendations, action.index, action.field, action.value), isDirty: true };

    case "SET_STEP":
      return { ...state, currentStep: action.step };

    case "MARK_SAVED":
      return { ...state, isDirty: false, lastSavedAt: action.savedAt, saveStatus: "saved", saveError: null };

    case "SET_SAVE_STATUS":
      return { ...state, saveStatus: action.status, saveError: action.status === "error" ? action.message ?? null : null };

    case "SET_VALIDATION_ERRORS":
      return { ...state, validationErrors: action.errors };

    case "LOAD_FROM_API":
      return loadFromDetail(state, action.detail);

    default:
      return state;
  }
}

function loadFromDetail(state: InspectionFormState, d: ArborInspectionDetail): InspectionFormState {
  return {
    ...state,
    locationConfirmed: d.locationConfirmed,
    botanicalIdentificationStatus: d.botanicalIdentificationStatus ?? "",
    botanicalNotes: d.botanicalNotes ?? "",
    inspectionDate: d.inspectionDate?.split("T")[0] ?? "",
    notes: d.notes ?? "",
    requiresMonitoring: d.requiresMonitoring,
    nextInspectionSuggestedAt: d.nextInspectionSuggestedAt ?? "",

    measurement: d.measurement
      ? {
          dapCm: d.measurement.dapCm,
          capCm: d.measurement.capCm,
          heightM: d.measurement.heightM,
          crownDiameterM: d.measurement.crownDiameterM,
          inclinationDeg: d.measurement.inclinationDeg,
          measurementSource: d.measurement.measurementSource,
        }
      : {},
    stems: d.stems.map((s) => ({
      sequence: s.sequence,
      capCm: s.capCm,
      dapCm: s.dapCm,
      measurementMethod: s.measurementMethod,
      heightM: s.heightM,
      notes: s.notes,
    })),

    healthAssessment: d.healthAssessment
      ? {
          generalStatus: d.healthAssessment.generalStatus,
          rootCondition: d.healthAssessment.rootCondition,
          rootNotes: d.healthAssessment.rootNotes,
          pests: d.healthAssessment.pests,
          diseases: d.healthAssessment.diseases,
          fungi: d.healthAssessment.fungi,
          termites: d.healthAssessment.termites,
          cavities: d.healthAssessment.cavities,
          rot: d.healthAssessment.rot,
          dryBranches: d.healthAssessment.dryBranches,
          exposedRoots: d.healthAssessment.exposedRoots,
          abnormalInclination: d.healthAssessment.abnormalInclination,
          inclinationAngleDeg: d.healthAssessment.inclinationAngleDeg,
          inclinationDirection: d.healthAssessment.inclinationDirection,
          exposedRootsDetail: d.healthAssessment.exposedRootsDetail,
          underminedRoots: d.healthAssessment.underminedRoots,
          damagedRoots: d.healthAssessment.damagedRoots,
          constructionDamage: d.healthAssessment.constructionDamage,
          soilCompaction: d.healthAssessment.soilCompaction,
          rootPlateMovement: d.healthAssessment.rootPlateMovement,
          notes: d.healthAssessment.notes,
        }
      : {},
    healthFindings: d.healthFindings.map((f) => ({
      findingType: f.findingType,
      location: f.location,
      severity: f.severity,
      notes: f.notes,
    })),

    mechanicalFindings: d.mechanicalFindings.map((f) => ({
      findingType: f.findingType,
      location: f.location,
      severity: f.severity,
      notes: f.notes,
    })),

    interferences: d.interferences.map((i) => ({
      interferenceType: i.interferenceType,
      intensity: i.intensity,
      description: i.description,
    })),

    riskAssessment: d.riskAssessment
      ? {
          riskClass: d.riskAssessment.riskClass,
          riskScore: d.riskAssessment.riskScore,
          priority: d.riskAssessment.priority,
          pedestrians: d.riskAssessment.pedestrians,
          vehicles: d.riskAssessment.vehicles,
          buildings: d.riskAssessment.buildings,
          powerGrid: d.riskAssessment.powerGrid,
          riskCriteria: d.riskAssessment.riskCriteria,
          targetClass: d.riskAssessment.targetClass,
          priorityClass: d.riskAssessment.priorityClass,
          notes: d.riskAssessment.notes,
        }
      : {},
    riskTargets: d.riskTargets.map((t) => ({
      targetType: t.targetType,
      occupancy: t.occupancy,
      notes: t.notes,
    })),

    diagnosis: d.diagnosis ?? "",
    technicalOpinion: d.technicalOpinion ?? "",
    recommendations: d.recommendations.map((r) => ({
      recommendationType: r.recommendationType,
      priority: r.priority,
      justification: r.justification,
    })),
    compensation: d.compensation
      ? {
          compensationIndicated: d.compensation.compensationIndicated,
          proposedSeedlingQuantity: d.compensation.proposedSeedlingQuantity,
          proposedSpecies: d.compensation.proposedSpecies,
          notes: d.compensation.notes,
        }
      : { compensationIndicated: false },
    compensationTouched: !!d.compensation,

    inspectorName: d.inspectorName ?? "",
    professionalCouncil: d.professionalCouncil ?? "",
    professionalRegistration: d.professionalRegistration ?? "",

    isDirty: false,
    lastSavedAt: new Date(d.updatedAt),
    saveStatus: "saved",
    saveError: null,
    validationErrors: {},
  };
}

// --- Build update payload from state ---

export function buildUpdatePayload(state: InspectionFormState): ArborInspectionUpdateInput {
  // Texto apagado vai como "" para o servidor LIMPAR o campo; null significava "nao mexa"
  // e o valor antigo voltava na proxima leitura.
  return {
    inspectionDate: state.inspectionDate || null,
    notes: state.notes,
    inspectorName: state.inspectorName,
    professionalCouncil: state.professionalCouncil,
    professionalRegistration: state.professionalRegistration,
    locationConfirmed: state.locationConfirmed,
    botanicalIdentificationStatus: state.botanicalIdentificationStatus || null,
    botanicalNotes: state.botanicalNotes,
    diagnosis: state.diagnosis,
    technicalOpinion: state.technicalOpinion,
    requiresMonitoring: state.requiresMonitoring,
    nextInspectionSuggestedAt: state.nextInspectionSuggestedAt || null,
    measurement: Object.keys(state.measurement).length > 0 ? state.measurement : null,
    // Listas e blocos vao SEMPRE: remover o ultimo item (ou desmarcar o ultimo indicador)
    // tem de apagar no servidor.  Antes a lista vazia virava null e a remocao se perdia.
    stems: state.stems,
    healthAssessment: state.healthAssessment,
    healthFindings: state.healthFindings,
    mechanicalFindings: state.mechanicalFindings,
    riskAssessment: state.riskAssessment,
    riskTargets: state.riskTargets,
    interferences: state.interferences,
    recommendations: state.recommendations,
    compensation: state.compensation.compensationIndicated || state.compensationTouched ? state.compensation : null,
  };
}

// --- Autosave hook ---

export function useInspectionForm(inspectionId: number | null) {
  const [state, dispatch] = useReducer(inspectionFormReducer, undefined, createInitialState);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Remontagem (StrictMode, recarga a quente) precisa religar a flag; sem isso o
    // salvamento ficava desligado para sempre apos a primeira desmontagem.
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const save = useCallback(async () => {
    if (!inspectionId || !state.isDirty) return;
    if (!isMountedRef.current) return;

    dispatch({ type: "SET_SAVE_STATUS", status: "saving" });
    try {
      const payload = buildUpdatePayload(state);
      const snakePayload = toSnakeCasePayload(payload as unknown as Record<string, unknown>);
      await inspectionApi.update(inspectionId, snakePayload as never);
      if (isMountedRef.current) {
        dispatch({ type: "MARK_SAVED", savedAt: new Date() });
      }
    } catch (err) {
      if (isMountedRef.current) {
        dispatch({ type: "SET_SAVE_STATUS", status: "error", message: err instanceof Error ? err.message : null });
      }
    }
  }, [inspectionId, state]);

  // Debounced autosave: 5s after last change
  useEffect(() => {
    if (!state.isDirty || !inspectionId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { save(); }, 5000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [state.isDirty, inspectionId, save]);

  return { state, dispatch, save };
}

// --- snake_case converter for API payloads ---

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function toSnakeCasePayload(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = camelToSnake(key);
    if (value === null || value === undefined) {
      result[snakeKey] = value;
    } else if (Array.isArray(value)) {
      result[snakeKey] = value.map((item) =>
        typeof item === "object" && item !== null
          ? toSnakeCasePayload(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === "object") {
      result[snakeKey] = toSnakeCasePayload(value as Record<string, unknown>);
    } else {
      result[snakeKey] = value;
    }
  }
  return result;
}
